// Fonctions communes aux dashboards (mêmes calculs que le dashboard des ventes de la page d'accueil)

export const MONTHS_FR = ["Jan", "Fév", "Mar", "Avr", "Mai", "Jun", "Jul", "Aoû", "Sep", "Oct", "Nov", "Déc"];

export const fmt3 = (v) => (v != null ? parseFloat(Number(v).toFixed(3)) : 0);

// Données {id: {month}, ...} -> tableau de 12 valeurs
export const byIdMonth = (data, field) => {
  const arr = Array(12).fill(0);
  (Array.isArray(data) ? data : []).forEach((item) => {
    const m = item?.id?.month;
    if (m >= 1 && m <= 12) arr[m - 1] = fmt3(item[field]);
  });
  return arr;
};

/**
 * Prévision par régression linéaire :
 * pour chaque mois sans donnée, on prédit la valeur d'après la tendance des mois renseignés.
 * Retourne 12 valeurs : la prévision pour les mois vides, 0 pour les mois réels.
 */
export const computePrevision = (arr) => {
  const points = [];
  arr.forEach((v, i) => {
    if (v > 0) points.push({ x: i + 1, y: v });
  });

  if (points.length < 2) {
    if (points.length === 1) {
      const avg = points[0].y;
      return arr.map((v) => (v > 0 ? 0 : fmt3(avg)));
    }
    return Array(12).fill(0);
  }

  const n = points.length;
  const sumX = points.reduce((s, p) => s + p.x, 0);
  const sumY = points.reduce((s, p) => s + p.y, 0);
  const sumXY = points.reduce((s, p) => s + p.x * p.y, 0);
  const sumX2 = points.reduce((s, p) => s + p.x * p.x, 0);
  const denom = n * sumX2 - sumX * sumX;

  if (denom === 0) {
    const avg = sumY / n;
    return arr.map((v) => (v > 0 ? 0 : fmt3(avg)));
  }

  const slope = (n * sumXY - sumX * sumY) / denom;
  const intercept = (sumY - slope * sumX) / n;
  const predict = (x) => fmt3(Math.max(0, slope * x + intercept));

  return arr.map((v, i) => (v > 0 ? 0 : predict(i + 1)));
};

/**
 * Histogramme comparatif :
 *  - année N : mois réels pleins, mois prévus en couleur claire
 *  - année N-1 : semi-transparent
 */
export const buildChart = (curr, curr_p, yearN, yearP, colorSolid, colorPrev, colorOld) => {
  const prevision = computePrevision(curr);
  const merged = curr.map((v, i) => (v > 0 ? v : prevision[i]));
  const bgColors = curr.map((v) => (v > 0 ? colorSolid : colorPrev));
  const bdColors = curr.map((v) => (v > 0 ? colorSolid : colorSolid.replace(/[\d.]+\)$/, "1)")));

  return {
    labels: MONTHS_FR,
    datasets: [
      {
        label: `${yearN} (prévision = barres claires)`,
        data: merged,
        backgroundColor: bgColors,
        borderColor: bdColors,
        borderWidth: 1,
      },
      {
        label: `${yearP}`,
        data: curr_p,
        backgroundColor: colorOld,
        borderWidth: 0,
      },
    ],
  };
};

export const evolution = (curr, prev) => {
  if (!prev || prev === 0) return null;
  return ((curr - prev) / Math.abs(prev)) * 100;
};

// Total des 12 mois en complétant les mois vides par la prévision
export const mergedSum = (actual, prev) =>
  actual.map((v, i) => (v > 0 ? v : prev[i])).reduce((a, b) => a + b, 0);

// goodWhenUp=false : une hausse est une mauvaise nouvelle (ex : factures non payées)
export const EvoBadge = ({ curr, prev, goodWhenUp = true }) => {
  const pct = evolution(curr, prev);
  if (pct === null) return null;
  const up = pct >= 0;
  const good = up === goodWhenUp;
  return (
    <small style={{ fontSize: "0.75rem", color: good ? "#4caf50" : "#f44336", fontWeight: 600 }}>
      <i className={`fas fa-arrow-${up ? "up" : "down"} mr-1`}></i>
      {Math.abs(pct).toFixed(1)}%
    </small>
  );
};

export const chartOpts = (title) => ({
  responsive: true,
  plugins: {
    legend: { position: "top" },
    title: { display: !!title, text: title, font: { size: 12 } },
    tooltip: {
      callbacks: {
        label: (ctx) => {
          const isActual = ctx.datasetIndex === 0;
          const label = ctx.dataset.label?.split(" (")[0] || "";
          if (isActual) {
            const rawBg = Array.isArray(ctx.dataset.backgroundColor)
              ? ctx.dataset.backgroundColor[ctx.dataIndex]
              : ctx.dataset.backgroundColor;
            const isPrevi = rawBg && rawBg.includes("0.25");
            return `${isPrevi ? "Prévision" : "Réel"} ${label} : ${fmt3(ctx.raw)}`;
          }
          return `${label} : ${fmt3(ctx.raw)}`;
        },
      },
    },
  },
  scales: { y: { beginAtZero: true } },
});
